/**
 * LoginPage
 * Renders the Authentication component and manages user session state.
 * Responsive, accessible, and uses semantic HTML.
 */
import React, { useEffect, useState } from 'react';
import Authentication from '../shared/component/Authentication';
import { getCurrentUser, subscribeToAuthState, login, register, logout, requestPasswordRecovery, type User } from '../services/graphql/authService';
import { updateCurrentUserProfile } from '../services/graphql/userService';
import { useLocation, useNavigate } from 'react-router-dom';
import '../styles/shared.css';

const LoginPage: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Extract redirect param from URL
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get('redirect');
  const sessionStorageRedirect = sessionStorage.getItem('postLoginRedirect');

  useEffect(() => {
    console.log('[LoginPage] Mounted.');
    console.log('[LoginPage] location.search:', location.search);
    console.log('[LoginPage] redirect param:', redirectParam);
    console.log('[LoginPage] sessionStorage postLoginRedirect:', sessionStorageRedirect);
  }, [location.search, redirectParam, sessionStorageRedirect]);

  useEffect(() => {
    // Check initial session
    getCurrentUser()
      .then((user) => {
        setCurrentUser(user);
        setAuthLoading(false);
        // If user is authenticated, handle redirect
        if (user) {
          let redirectTo = redirectParam || sessionStorageRedirect || '/';
          if (sessionStorageRedirect) {
            sessionStorage.removeItem('postLoginRedirect');
            console.log('[LoginPage] Removed postLoginRedirect from sessionStorage');
          }
          console.log('[LoginPage] Navigating after login to:', redirectTo);
          navigate(redirectTo, { replace: true });
        }
      })
      .catch(() => {
        setCurrentUser(null);
        setAuthLoading(false);
      });

    // Listen for auth state changes
    const unsubscribe = subscribeToAuthState((user) => {
      console.log('[LoginPage] Auth state changed:', user ? 'logged in' : 'logged out');
      setCurrentUser(user);

      if (user) {
        let redirectTo = redirectParam || sessionStorageRedirect || '/';
        if (sessionStorageRedirect) {
          sessionStorage.removeItem('postLoginRedirect');
        }
        console.log('[LoginPage] Navigating after login to:', redirectTo);
        navigate(redirectTo, { replace: true });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [redirectParam, sessionStorageRedirect, navigate]);

  const handleLogout = async () => {
    setAuthLoading(true);
    try {
      await logout();
      setCurrentUser(null);
      console.log('[LoginPage] User logged out.');
    } catch (err) {
      setAuthError((err as Error).message);
      console.error('[LoginPage] Logout error:', err);
    } finally {
      setAuthLoading(false);
    }
  };

  useEffect(() => {
    console.log('[LoginPage] Rendering Authentication component. currentUser:', currentUser, 'authLoading:', authLoading, 'authError:', authError);
  }, [currentUser, authLoading, authError]);

  // --- Handlers for shared Authentication component ---
  const handleLogin = async (email: string, password: string) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const { user, error } = await login(email, password);
      if (error) {
        setAuthError(error.message || 'Login failed');
        return;
      }
      setCurrentUser(user);
    } catch (err: any) {
      setAuthError(err.message || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async ({ email, password, firstName, lastName }: any) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      const { user, error } = await register(email, password, fullName);

      if (error) {
        setAuthError(error.message || 'Registration failed');
        return;
      }

      setCurrentUser(user);
      setAuthError('Registration successful! You are now logged in.');
    } catch (err: any) {
      setAuthError(err.message || 'Registration failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handlePasswordRecovery = async (email: string) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const { error } = await requestPasswordRecovery(email);
      if (error) {
        setAuthError(error.message || 'Password recovery failed');
        return;
      }
      setAuthError('If this email exists, a password reset link has been sent.');
    } catch (err: any) {
      setAuthError(err.message || 'Password recovery failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleProfileUpdate = async ({ firstName, lastName, phoneNumber, countryCode }: any) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const { error } = await updateCurrentUserProfile({
        firstName,
        lastName,
        phoneNumber: phoneNumber || '',
        countryCode: countryCode || ''
      });

      if (error) {
        setAuthError(error.message || 'Profile update failed');
        return;
      }

      // Refresh user data
      const user = await getCurrentUser();
      setCurrentUser(user);
    } catch (err: any) {
      setAuthError(err.message || 'Profile update failed');
    } finally {
      setAuthLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <section className="w-full max-w-md mx-auto p-6 bg-card rounded-lg shadow-lg">
        <Authentication
          currentUser={currentUser}
          onLogin={handleLogin}
          onRegister={handleRegister}
          onLogout={handleLogout}
          onProfileUpdate={handleProfileUpdate}
          onPasswordRecovery={handlePasswordRecovery}
          loading={authLoading}
          error={authError}
        />
      </section>
    </main>
  );
};

export default LoginPage;
